import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Lightbulb, X } from 'lucide-react';
import { api } from '../lib/api';

interface Tip {
  id: number;
  text: string;
  category: string | null;
}

interface GeneratingTipsPopupProps {
  isOpen: boolean;
  darkMode: boolean;
  /** Called when generation completes or errors – parent must close */
  error?: string | null;
  /** Wenn gesetzt: zeigt im Fehlerfall einen „Zurücksetzen"-Button, der eine
   *  hängende Generierung freigibt (löst „bereits in Generierung"). */
  onReset?: () => void;
  /** Wenn gesetzt: zeigt während der laufenden Generierung einen „Abbrechen"-Button. */
  onCancel?: () => void;
}

/**
 * Popup that shows rotating tips while AI text is being generated.
 * Used for non-admin users. Tips are fetched from /api/tips and rotate every 10s.
 * Each generation starts with a random tip.
 */
export function GeneratingTipsPopup({ isOpen, darkMode, error, onReset, onCancel }: GeneratingTipsPopupProps) {
  const [tips, setTips] = useState<Tip[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fadeState, setFadeState] = useState<'in' | 'out'>('in');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tipsLoaded = useRef(false);

  // Fetch tips once
  useEffect(() => {
    if (tipsLoaded.current) return;
    tipsLoaded.current = true;
    api.get<Tip[]>('/api/tips')
      .then(data => {
        if (data && data.length > 0) {
          setTips(data);
          // Start at a random index each time
          setCurrentIndex(Math.floor(Math.random() * data.length));
        }
      })
      .catch(() => {
        // Silently fail - tips are nice-to-have
      });
  }, []);

  // Rotate tips every 10 seconds
  useEffect(() => {
    if (!isOpen || tips.length <= 1) return;

    intervalRef.current = setInterval(() => {
      // Fade out
      setFadeState('out');
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % tips.length);
        setFadeState('in');
      }, 400); // 400ms fade-out before switching
    }, 10000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isOpen, tips.length]);

  // Reset random start each time popup opens
  useEffect(() => {
    if (isOpen && tips.length > 0) {
      setCurrentIndex(Math.floor(Math.random() * tips.length));
      setFadeState('in');
    }
  }, [isOpen, tips.length]);

  if (!isOpen) return null;

  const currentTip = tips[currentIndex];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div
        className={`relative w-full max-w-lg flex flex-col rounded-xl shadow-2xl ${
          darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
        } border`}
      >
        {/* Header */}
        <div
          className={`flex items-center gap-3 p-5 border-b ${
            darkMode ? 'border-slate-700' : 'border-slate-200'
          }`}
        >
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-500 rounded-lg flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Wussten Sie schon?
            </h2>
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Tipps & Hinweise zur BAFA-Beratung
            </p>
          </div>
        </div>

        {/* Tip Content */}
        <div className="flex-1 p-5 sm:p-6 min-h-[160px] flex items-center">
          {currentTip ? (
            <div
              className={`transition-opacity duration-400 ${
                fadeState === 'in' ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ transitionDuration: '400ms' }}
            >
              {currentTip.category && (
                <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full mb-3 ${
                  darkMode
                    ? 'bg-blue-900/40 text-blue-300'
                    : 'bg-blue-50 text-blue-600'
                }`}>
                  {currentTip.category}
                </span>
              )}
              <p className={`text-sm leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                {currentTip.text}
              </p>
            </div>
          ) : (
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Text wird generiert...
            </p>
          )}
        </div>

        {/* Tip indicators (dots) */}
        {tips.length > 1 && (
          <div className="flex justify-center gap-1.5 px-5 pb-3">
            {tips.map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  i === currentIndex
                    ? 'bg-blue-500 w-4'
                    : darkMode
                      ? 'bg-slate-600'
                      : 'bg-slate-300'
                }`}
              />
            ))}
          </div>
        )}

        {/* Footer with generating status */}
        <div
          className={`p-4 sm:p-5 border-t ${
            darkMode ? 'border-slate-700' : 'border-slate-200'
          }`}
        >
          {error ? (
            <div className="flex flex-col gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              {onReset && (
                <button
                  type="button"
                  onClick={onReset}
                  className="self-start text-sm font-medium px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors"
                >
                  Generierung zurücksetzen
                </button>
              )}
            </div>
          ) : (
            <div className={`flex items-center gap-3 p-3 rounded-lg ${
              darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
            } border`}>
              <Loader2 className={`w-5 h-5 animate-spin flex-shrink-0 ${
                darkMode ? 'text-blue-400' : 'text-blue-600'
              }`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  Claude generiert Text...
                </p>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-blue-400/70' : 'text-blue-500'}`}>
                  Dies kann bis zu 3 Minuten dauern.
                </p>
              </div>
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${
                    darkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                  }`}
                >
                  Abbrechen
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
